/** The signed-in user (`GET /v1/me`), the company the dashboard is showing (sent as `X-Organization-Id` on every
 * request) and what the user's role there allows (`can`). Platform admins may also open any company without a
 * membership ("viewing as platform admin"); the API audits what they change there. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { api, setActiveOrganization, unwrap, type S } from "./client";

const ORG_KEY = "solaros.org";
const ADMIN_ORG_KEY = "solaros.adminOrg";

export type Area = "designs_leads" | "branding" | "widget_keys" | "credits_billing" | "team" | "settings_rules" | "integrations" | "audit_log";
export type Action = "view" | "create" | "edit" | "delete";

interface OrgState {
  me: S["Me"];
  isPlatformAdmin: boolean;
  memberships: S["Membership"][];
  /** The caller's membership in the shown company (null when a platform admin views it without one). */
  membership: S["Membership"] | null;
  orgId: string | null;
  orgName: string;
  role: S["MembershipRole"] | null;
  /** A platform admin is viewing a company they are not a member of. */
  adminView: boolean;
  /** Whether the user may do `action` in `area` of the shown company (the API enforces the same matrix). */
  can: (area: Area, action: Action) => boolean;
  selectOrg: (id: string) => void;
  openAsAdmin: (id: string) => Promise<void>;
  exitAdminView: () => void;
}

const OrgContext = createContext<OrgState | null>(null);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => unwrap(api.GET("/v1/me")), staleTime: 60_000 });
}

export function OrgProvider({ me, children }: { me: S["Me"]; children: ReactNode }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(() => read(ORG_KEY));
  const isPlatformAdmin = me.platform_role === "platform_admin" || me.user.platform_role === "platform_admin";
  const [adminOrg, setAdminOrg] = useState<string | null>(() => (isPlatformAdmin ? read(ADMIN_ORG_KEY) : null));
  const memberships = me.memberships;
  const own = memberships.find((m) => m.organization_id === selected) ?? memberships[0] ?? null;
  const viewed = adminOrg ? memberships.find((m) => m.organization_id === adminOrg) ?? null : own;
  const orgId = adminOrg ?? own?.organization_id ?? null;
  // Set before any child query runs, so every request names the company shown.
  setActiveOrganization(orgId);

  const adminOrgInfo = useQuery({
    queryKey: ["org", adminOrg],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}", { params: { path: { org_id: adminOrg! } } })),
    enabled: !!adminOrg && !viewed,
  });

  const value = useMemo<OrgState>(() => {
    const adminView = !!adminOrg && !viewed;
    const permissions = new Set(viewed?.permissions ?? []);
    return {
      me,
      isPlatformAdmin,
      memberships,
      membership: viewed,
      orgId,
      orgName: viewed?.organization_name ?? adminOrgInfo.data?.name ?? "…",
      role: viewed?.role ?? null,
      adminView,
      can: (area, action) => isPlatformAdmin || permissions.has(`${area}:${action}`),
      selectOrg: (id: string) => {
        write(ORG_KEY, id);
        write(ADMIN_ORG_KEY, null);
        setAdminOrg(null);
        setSelected(id);
      },
      openAsAdmin: async (id: string) => {
        await unwrap(api.POST("/v1/admin/orgs/{org_id}/open", { params: { path: { org_id: id } } }));
        write(ADMIN_ORG_KEY, id);
        setAdminOrg(id);
        qc.invalidateQueries();
      },
      exitAdminView: () => {
        write(ADMIN_ORG_KEY, null);
        setAdminOrg(null);
      },
    };
  }, [me, isPlatformAdmin, memberships, viewed, orgId, adminOrg, adminOrgInfo.data, qc]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg outside OrgProvider");
  return ctx;
}

/** The current organisation id; pages under the org shell always have one. */
export function useOrgId(): string {
  const { orgId } = useOrg();
  if (!orgId) throw new Error("No organisation selected");
  return orgId;
}

export const ROLE_TONE: Record<S["MembershipRole"], "grad" | "info" | "ok" | "warn" | undefined> = {
  owner: "grad",
  admin: "info",
  designer: "ok",
  sales: "warn",
  viewer: undefined,
};
